/**
 * Scheduled Templates Component
 * Lista dei template programmati per invio futuro
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { confirmAsync } from '../../utils/notify';

const ScheduledTemplates = () => {
  const { t } = useTranslation();
  
  const [scheduled, setScheduled] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, sent: 0, cancelled: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchScheduled = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await api.get(`/api/v1/templates/scheduled?${params.toString()}`);
      setScheduled(response.data.scheduled);
      setStats({
        total: response.data.total,
        pending: response.data.pending,
        sent: response.data.sent,
        cancelled: response.data.cancelled,
        failed: response.data.failed
      });
    } catch (err) {
      setError(getApiErrorMessage(err, t('templates.scheduled.errors.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    fetchScheduled();
  }, [fetchScheduled]);

  const handleCancel = async (id) => {
    if (!(await confirmAsync({ message: t('templates.scheduled.confirmCancel'), danger: true }))) return;
    
    try {
      await api.delete(`/api/v1/templates/scheduled/${id}`);
      fetchScheduled();
    } catch (err) {
      setError(getApiErrorMessage(err, t('templates.scheduled.errors.cancelFailed')));
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'sent': return '✅';
      case 'cancelled': return '❌';
      case 'failed': return '⚠️';
      default: return '•';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'pending': return 'pending';
      case 'sent': return 'approved';
      case 'cancelled': return '';
      case 'failed': return 'rejected';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="scheduled-list">
      {/* Stats Mini Cards */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ padding: '0.75rem 1.25rem', background: '#f9fafb', borderRadius: '8px' }}>
          <strong>{stats.pending}</strong> <span style={{ color: '#666' }}>{t('templates.scheduled.stats.pending')}</span>
        </div>
        <div style={{ padding: '0.75rem 1.25rem', background: '#dcfce7', borderRadius: '8px' }}>
          <strong>{stats.sent}</strong> <span style={{ color: '#666' }}>{t('templates.scheduled.stats.sent')}</span>
        </div>
        <div style={{ padding: '0.75rem 1.25rem', background: '#fee2e2', borderRadius: '8px' }}>
          <strong>{stats.failed}</strong> <span style={{ color: '#666' }}>{t('templates.scheduled.stats.failed')}</span>
        </div>
      </div>

      {/* Filter */}
      <div style={{ marginBottom: '1.5rem' }}>
        <select 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">{t('templates.scheduled.filters.all')}</option>
          <option value="pending">{t('templates.scheduled.filters.pending')}</option>
          <option value="sent">{t('templates.scheduled.filters.sent')}</option>
          <option value="cancelled">{t('templates.scheduled.filters.cancelled')}</option>
          <option value="failed">{t('templates.scheduled.filters.failed')}</option>
        </select>
      </div>

      {error && (
        <div className="error-banner" style={{ marginBottom: '1rem' }}>
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* List */}
      {scheduled.length === 0 ? (
        <div className="empty-state">
          <div className="icon">⏰</div>
          <h3>{t('templates.scheduled.empty.title')}</h3>
          <p>{t('templates.scheduled.empty.description')}</p>
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="template-list-header" style={{ gridTemplateColumns: '2fr 1fr 1.5fr 1fr 80px' }}>
            <span>{t('templates.scheduled.list.template')}</span>
            <span>{t('templates.scheduled.list.recipient')}</span>
            <span>{t('templates.scheduled.list.scheduledFor')}</span>
            <span>{t('templates.scheduled.list.status')}</span>
            <span></span>
          </div>

          {/* Items */}
          {scheduled.map((item) => (
            <div key={item.id} className="scheduled-item" style={{ gridTemplateColumns: '2fr 1fr 1.5fr 1fr 80px' }}>
              <div className="scheduled-time">
                {item.template_name || `Template #${item.template_id}`}
                <span>{Object.values(item.variables || {}).slice(0, 2).join(', ')}</span>
              </div>
              
              <div>
                <div style={{ fontWeight: 500 }}>{item.recipient_name || '-'}</div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>{item.recipient_phone}</div>
              </div>
              
              <div className="scheduled-time">
                {formatDateTime(item.scheduled_for)}
              </div>
              
              <div>
                <span className={`status-badge ${getStatusClass(item.status)}`}>
                  {getStatusIcon(item.status)} {t(`templates.scheduled.status.${item.status}`)}
                </span>
              </div>
              
              <div>
                {item.status === 'pending' && (
                  <button 
                    className="btn-delete"
                    onClick={() => handleCancel(item.id)}
                    title={t('templates.scheduled.cancel')}
                  >
                    ❌
                  </button>
                )}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default ScheduledTemplates;
